import { useEffect, useState } from "react";
import { useNoiseFilter } from "../../store/noiseFilter/hooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Transition } from "../../pages/app/utils/animation";
import { useEditor } from "../../store/editor/hooks";
import { useAnnotations } from "../../store/annotations/hooks";
import { useModals } from "../../store/dialogs/hooks";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { SNG_FFT_LENGTH } from "../../pages/app/utils/constants";

const SngSchema = z.object({
  noiseStart: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be at least 0."),
  ),
  noiseEnd: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be at least 0."),
  ),
  fftLength: z.number().optional(),
  noiseCancelRatio: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be between 0.0 and 1.0.")
      .max(1, "Must be between 0.0 and 1.0."),
  ),
  nStdThreshold: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be at least 0."),
  ),
  fdMaskAveraging: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be at least 0."),
  ),
  tdMaskAveraging: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        message: "Please enter a valid number.",
      })
      .min(0, "Must be at least 0."),
  ),
});

export const SngSettingDialogNEW = ({ onRun }) => {
  const { updateNoiseFilter } = useNoiseFilter();
  const { closeSngFilterDialogNEW, showSngFilterDialogNEW } = useModals();
  const { selectedTag } = useEditor();
  const { annotations } = useAnnotations();
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);

  const {
    control,
    handleSubmit,
    reset: rhfReset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    resolver: zodResolver(SngSchema),
    values: {
      noiseStart: selectedAnnotation
        ? parseFloat(selectedAnnotation.start.toFixed(3))
        : 0,
      noiseEnd: selectedAnnotation
        ? parseFloat(selectedAnnotation.end.toFixed(3))
        : 2,
      fftLength: 512,
      noiseCancelRatio: 0.75,
      nStdThreshold: 1.5,
      fdMaskAveraging: 500,
      tdMaskAveraging: 50,
    },
    mode: "all",
  });

  const handleClose = () => {
    rhfReset();
    closeSngFilterDialogNEW();
  };

  const onSubmit = (data) => {
    updateNoiseFilter(data);
    onRun && onRun();
    rhfReset();
    handleClose();
  };

  useEffect(() => {
    setSelectedAnnotation(annotations.find((item) => item.tag === selectedTag));
  }, [annotations, showSngFilterDialogNEW, selectedTag]);

  return (
    <Dialog
      aria-hidden={!showSngFilterDialogNEW}
      open={showSngFilterDialogNEW}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Noise Gate (Spectral)</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 4 }}>
            Please edit the settings below as needed to run the noise filter. If
            you are unsure of the settings, try using the default values.
          </DialogContentText>
          <Grid container columnSpacing={2} rowSpacing={4}>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="noiseStart"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    error={!!errors.noiseStart}
                    helperText={
                      errors.noiseStart
                        ? errors.noiseStart.message
                        : "The starting point in audio file for noise reference."
                    }
                    label="Noise Sample Start"
                    slotProps={{
                      htmlInput: { readOnly: true },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">s</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="noiseEnd"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.noiseEnd}
                    helperText={
                      errors.noiseEnd
                        ? errors.noiseEnd.message
                        : "The stopping point in audio file for noise reference."
                    }
                    label="Noise Sample Stop"
                    type="number"
                    slotProps={{
                      htmlInput: { readOnly: true },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">s</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl error={!!errors.fftLength}>
                <InputLabel id="fft-length-label">FFT Length</InputLabel>
                <Controller
                  name="fftLength"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      labelId="fft-length-label"
                      id="fft-length-select"
                      label="FFT Length"
                    >
                      {SNG_FFT_LENGTH.map((option) => (
                        <MenuItem key={option.id} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>
                  {errors.fftLength
                    ? errors.fftLength.message
                    : "The size of FFT to perform (must be a power of 2)."}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="noiseCancelRatio"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Noise Cancellation Ratio"
                    type="number"
                    fullWidth
                    helperText={
                      errors.noiseCancelRatio
                        ? errors.noiseCancelRatio?.message
                        : "The noise cancellation ratio (0.0 to 1.0)."
                    }
                    error={!!errors.noiseCancelRatio}
                    slotProps={{
                      htmlInput: { min: 0, max: 1, step: 0.05 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="nStdThreshold"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Std Threshold"
                    type="number"
                    helperText={
                      errors.nStdThreshold
                        ? errors.nStdThreshold.message
                        : "The standard deviation above mean for threshold."
                    }
                    error={!!errors.nStdThreshold}
                    slotProps={{ htmlInput: { min: 0, step: 0.05 } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="fdMaskAveraging"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Mask Smoothing (Frequency)"
                    helperText={
                      errors.fdMaskAveraging
                        ? errors.fdMaskAveraging.message
                        : "The mask smoothing frequency range in Hz."
                    }
                    error={!!errors.fdMaskAveraging}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">Hz</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="tdMaskAveraging"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Mask Smoothing (Time)"
                    helperText={
                      errors.tdMaskAveraging
                        ? errors.tdMaskAveraging.message
                        : "The mask smoothing time range in milliseconds."
                    }
                    error={!!errors.tdMaskAveraging}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">ms</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!isDirty}
            onClick={() => rhfReset()}
            variant="outlined"
          >
            Reset to Default
          </Button>
          <Button disabled={!isValid} type="submit" variant="contained">
            Run
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
